import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";

export default function ScoreboardPanel() {
  const [board, setBoard] = useState(null);
  const [upNextText, setUpNextText] = useState("");
  const [timer, setTimer] = useState(null);
  const [preset, setPreset] = useState("600");

  const load = useCallback(async () => {
    const [br, tr] = await Promise.all([api.get("/scoreboard"), api.get("/timer")]);
    setBoard(br.data);
    setUpNextText((br.data.up_next || []).join("\n"));
    setTimer(tr.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = async (updates) => {
    try {
      const { data } = await api.patch("/admin/scoreboard", updates);
      setBoard(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const timerAction = async (action, extra = {}) => {
    try {
      const { data } = await api.post("/admin/timer", { action, ...extra });
      setTimer(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!board) return <p className="text-white/50">Chargement…</p>;

  const PRESET_LABELS = { 600: "Poule 10 min", 720: "Finale 12 min", 360: "Foot 6 min" };

  return (
    <div className="space-y-6" data-testid="scoreboard-panel">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-xs uppercase tracking-widest text-white/50">
          Tableau indépendant — s'affiche sur /tableau en moins de 3 s
        </span>
        <a href="/tableau" target="_blank" rel="noopener noreferrer" className="btn-brutal !py-2 !px-4 !text-xs ml-auto" data-testid="open-tableau-btn">
          📺 Ouvrir le tableau
        </a>
      </div>

      {/* Match en cours */}
      <div className="uf-card p-6">
        <h3 className="font-display text-2xl mb-4 text-[#FF6B1A]">MATCH AFFICHÉ</h3>
        <input
          className="uf-input w-full mb-4"
          placeholder="Titre (ex : Poule A · Match 3, Finale Basket…)"
          defaultValue={board.label || ""}
          onBlur={(e) => e.target.value !== (board.label || "") && patch({ label: e.target.value })}
          data-testid="board-label-input"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {["a", "b"].map((side) => (
            <div key={side} className="text-center">
              <input
                className="uf-input w-full text-center font-bold mb-3"
                placeholder={`Nom équipe ${side.toUpperCase()}`}
                defaultValue={board[`team_${side}`]}
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v && v !== board[`team_${side}`]) patch({ [`team_${side}`]: v });
                }}
                data-testid={`board-team-${side}-input`}
              />
              <p className="font-display text-6xl text-[#FF6B1A] mb-3" data-testid={`board-score-${side}`}>{board[`score_${side}`]}</p>
              <div className="flex justify-center gap-2 flex-wrap">
                {[1, 2, 3].map((d) => (
                  <button
                    key={d}
                    onClick={() => patch({ [`score_${side}`]: board[`score_${side}`] + d })}
                    className="w-10 h-10 rounded-full bg-[#FF6B1A] text-black font-black hover:brightness-110"
                    data-testid={`board-plus${d}-${side}`}
                  >
                    +{d}
                  </button>
                ))}
                <button
                  onClick={() => patch({ [`score_${side}`]: Math.max(0, board[`score_${side}`] - 1) })}
                  className="w-10 h-10 rounded-full border border-white/25 text-white/60 font-black hover:text-white"
                  data-testid={`board-minus-${side}`}
                >
                  −1
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="text-center mt-5">
          <button
            onClick={() => patch({ score_a: 0, score_b: 0 })}
            className="text-xs uppercase tracking-widest border border-[#FF3B30]/60 text-[#FF3B30] px-4 py-2 rounded-full hover:bg-[#FF3B30] hover:text-black transition-colors"
            data-testid="board-reset-scores-btn"
          >
            Remettre 0 - 0
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Chrono */}
        <div className="uf-card p-6">
          <h3 className="font-display text-2xl mb-4 text-[#FFB800]">CHRONO</h3>
          <div className="flex items-center gap-2 flex-wrap">
            <select className="uf-input !py-2 !px-3 text-xs" value={preset} onChange={(e) => setPreset(e.target.value)} data-testid="board-timer-preset">
              <option value="600">Poule — 10 min</option>
              <option value="720">Finale — 12 min</option>
              <option value="360">Foot — 6 min</option>
            </select>
            <button onClick={() => timerAction("start", { duration: Number(preset), label: PRESET_LABELS[preset] })} className="text-xs uppercase tracking-widest font-bold bg-[#2EE59D] text-black px-3 py-2 rounded-full hover:brightness-110" data-testid="board-timer-start">
              ▶ Démarrer
            </button>
            {timer?.running ? (
              <button onClick={() => timerAction("pause")} className="text-xs uppercase tracking-widest font-bold bg-[#FF9F0A] text-black px-3 py-2 rounded-full hover:brightness-110" data-testid="board-timer-pause">⏸ Pause</button>
            ) : timer?.paused_remaining !== null && timer?.paused_remaining !== undefined ? (
              <button onClick={() => timerAction("resume")} className="text-xs uppercase tracking-widest font-bold bg-[#FFB800] text-black px-3 py-2 rounded-full hover:brightness-110" data-testid="board-timer-resume">▶ Reprendre</button>
            ) : null}
            <button onClick={() => timerAction("reset")} className="text-xs uppercase tracking-widest border border-[#FF3B30]/60 text-[#FF3B30] px-3 py-2 rounded-full hover:bg-[#FF3B30] hover:text-black transition-colors" data-testid="board-timer-reset">✕ Stop</button>
          </div>
          <p className="text-xs text-white/40 mt-3">Le chrono est partagé entre /tableau et /live.</p>
        </div>

        {/* À suivre */}
        <div className="uf-card p-6">
          <h3 className="font-display text-2xl mb-4 text-[#2EE59D]">ÉQUIPES À SUIVRE</h3>
          <textarea
            className="uf-input w-full !h-32 text-sm"
            placeholder={"Un match par ligne, ex :\nLes Lions vs Zola Stars\nRivoli Kings vs Street Boyz"}
            value={upNextText}
            onChange={(e) => setUpNextText(e.target.value)}
            data-testid="board-upnext-input"
          />
          <button
            onClick={() => {
              patch({ up_next: upNextText.split("\n").map((l) => l.trim()).filter(Boolean) });
              toast.success("Liste 'À suivre' mise à jour");
            }}
            className="btn-brutal !py-2 !px-4 !text-xs mt-3"
            data-testid="board-upnext-save-btn"
          >
            Enregistrer la liste
          </button>
        </div>
      </div>
    </div>
  );
}
